import React from 'react';
import useAuth from '../hooks/useAuth';
import { DEFAULT_PRICING_SETTINGS, normalizePricingSettings } from '../utils/csomagStyleEstimate';

const INQ_ORANGE = '#f7931d';
const PAGE_SIZE = 10;

function statusLabel(status) {
  if (status === 'pending') return 'Függőben';
  if (status === 'rejected') return 'Elutasítva';
  if (status === 'offer_sent') return 'Ajánlat elküldve (nettó + ÁFA)';
  if (status === 'edited_offer_sent') return 'Módosított ajánlat elküldve';
  return status || '—';
}

function statusBadgeClass(status) {
  if (status === 'pending') return 'inq-badge inq-badge--pending';
  if (status === 'rejected') return 'inq-badge inq-badge--rejected';
  if (status === 'offer_sent' || status === 'edited_offer_sent') return 'inq-badge inq-badge--sent';
  return 'inq-badge';
}

function isOfferSentStatus(status) {
  return status === 'offer_sent' || status === 'edited_offer_sent';
}

function isOrderedItem(item) {
  return Boolean(item?.orderSubmittedAt);
}

function isOrderCompleted(item) {
  return Boolean(item?.orderCompletedAt);
}

function orderCompletionLabel(item) {
  return isOrderCompleted(item) ? 'Teljesítve' : 'Nem teljesítve';
}

function orderCompletionBadgeClass(item) {
  return isOrderCompleted(item) ? 'inq-badge inq-badge--completed' : 'inq-badge inq-badge--not-completed';
}

function insuranceHufForItem(item) {
  if (item?.finalOfferInsuranceHuf != null) return Math.max(0, Math.round(Number(item.finalOfferInsuranceHuf) || 0));
  if (item?.status === 'pending' && item?.calculatedInsuranceHuf != null) {
    return Math.max(0, Math.round(Number(item.calculatedInsuranceHuf) || 0));
  }
  return 0;
}

/** Admin szűrő + táblázat: ismert űrlap-típusok (megegyezik a backend inquiryType értékekkel). */
const SERVICE_TYPE_LABELS = {
  csomagszallitas: 'Csomagszállítás',
  butor_szallitas: 'Bútorszállítás',
  kerekpar_szallitas: 'Kerékpárszállítás',
  raklapos_szallitas: 'Raklapos szállítás',
  koltoztetes: 'Költöztetés',
  ikea_hazhozszallitas: 'IKEA házhozszállítás',
};

function serviceLabel(type) {
  const key = String(type || '').trim();
  if (SERVICE_TYPE_LABELS[key]) return SERVICE_TYPE_LABELS[key];
  return valueOrDash(type);
}

function serviceTypeCell(item) {
  const typeKey = String(item?.inquiryType || '').trim();
  const mainLabel = SERVICE_TYPE_LABELS[typeKey];
  const ship = item?.formPayload && typeof item.formPayload === 'object' ? item.formPayload.shipment : null;
  if (ship && typeof ship === 'object') {
    const detailParts = [ship.packaging, ship.content]
      .map((s) => String(s || '').trim())
      .filter(Boolean);
    const detail = detailParts.join(' · ');
    if (mainLabel) {
      if (detail) return `${mainLabel} — ${detail}`;
      return mainLabel;
    }
    const parts = [ship.type, ship.packaging, ship.content]
      .map((s) => String(s || '').trim())
      .filter(Boolean);
    if (parts.length) return parts.join(' · ');
  }
  const summary = String(item?.itemsSummary || '').trim();
  if (summary) return summary;
  return serviceLabel(item?.inquiryType);
}

function emailSentLabel(sentAt, err) {
  if (sentAt) return 'Kiküldve';
  if (err) return 'Hiba';
  return '—';
}

function valueOrDash(value) {
  const text = String(value || '').trim();
  return text || '—';
}

/** Calendar date in Europe/Budapest as YYYY-MM-DD (no time). */
function formatYmd(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Budapest',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

/** YYYY-MM-DD and HH:mm (Budapest), for e-mail küldés időpontja. */
function formatYmdHm(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const date = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Budapest',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
  const time = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Budapest',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(d);
  return `${date} ${time}`;
}

function formatHuf(n) {
  if (n == null || n === '' || Number.isNaN(Number(n))) return '—';
  return `${Math.round(Number(n)).toLocaleString('hu-HU')} Ft`;
}

/** Admin: amounts are nettó; show explicit + ÁFA after the figure. */
function formatHufNetto(n) {
  if (Number(n) === 0) return 'Egyedi ajánlat';
  const base = formatHuf(n);
  if (base === '—') return '—';
  return `${base} + ÁFA`;
}

function shortLine(text, max = 48) {
  const s = String(text || '').replace(/\s+/g, ' ').trim();
  if (s.length <= max) return s || '—';
  return `${s.slice(0, max - 1)}…`;
}

function routeLine(item) {
  const from = shortLine(item.pickupAddress, 22);
  const to = shortLine(item.deliveryAddress, 22);
  if (from === '—' && to === '—') return '—';
  return `${from} → ${to}`;
}

function adminTablePrice(item) {
  if (item?.finalOfferPrice != null) return formatHufNetto(item.finalOfferPrice);
  return formatHufNetto(item?.estimatedPrice);
}

function shipmentSizeLineFromShipment(shipment) {
  const s = shipment && typeof shipment === 'object' ? shipment : {};
  const l = String(s.lengthCm || '').trim();
  const w = String(s.widthCm || '').trim();
  const h = String(s.heightCm || '').trim();
  const sizeCm = String(s.sizeCm || '').trim();
  const volumeM3 = String(s.volumeM3 || '').trim();
  const parts = [];

  if (l && w && h) {
    parts.push(`${l} × ${w} × ${h} cm`);
  } else if (sizeCm && !/érték/i.test(sizeCm)) {
    parts.push(sizeCm.includes('cm') ? sizeCm : `${sizeCm} cm`);
  }
  if (volumeM3) parts.push(`${volumeM3} m³`);
  return parts.join(' · ');
}

function shipmentSizeLine(item) {
  const p = item?.formPayload && typeof item.formPayload === 'object' ? item.formPayload : {};
  const shipments = Array.isArray(p.shipments) && p.shipments.length ? p.shipments : p.shipment ? [p.shipment] : [];
  const lines = shipments.map(shipmentSizeLineFromShipment).filter(Boolean);
  if (lines.length > 1) return lines.map((line, index) => `${index + 1}. küldemény: ${line}`).join('\n');
  if (lines.length === 1) return lines[0];
  return valueOrDash(item?.dimensions);
}

/** Űrlapból: ügyfél kért rakodást (inhome) és/vagy biztosítást. */
function ugyfelExtraFlags(formPayload) {
  const p = formPayload && typeof formPayload === 'object' ? formPayload : {};
  const schedule = p.schedule || {};
  const insurance = p.insurance || {};
  return {
    wantsRakodas: schedule.deliveryMode === 'inhome',
    wantsInsurance: insurance.enabled === true,
  };
}

/** Beküldött csatolmányok fájlnevei (űrlap / formPayload). */
function attachmentNamesFromFormPayload(formPayload) {
  const p = formPayload && typeof formPayload === 'object' ? formPayload : {};
  const raw = p.attachmentNames;
  if (!Array.isArray(raw)) return [];
  return raw.map((x) => String(x || '').trim()).filter(Boolean);
}

function ugyfelUrlapPillStyle(wanted) {
  return {
    fontSize: 12,
    fontWeight: 700,
    padding: '3px 9px',
    borderRadius: 8,
    background: wanted ? '#fff7ed' : '#f3f4f6',
    color: wanted ? '#9a3412' : '#4b5563',
    border: `1px solid ${wanted ? '#fed7aa' : '#e5e7eb'}`,
    whiteSpace: 'nowrap',
  };
}

function parseHufInput(raw) {
  const digits = String(raw || '').replace(/\s/g, '').replace(/[^\d]/g, '');
  if (!digits) return 0;
  const n = parseInt(digits, 10);
  return Number.isFinite(n) && n >= 0 ? n : NaN;
}

/** Csak 0–9; üres string megmarad (törölhetsz mindent, majd új számot írsz). */
function filterHufDigitsInput(raw) {
  return String(raw ?? '').replace(/\D/g, '');
}

export default function AdminQuoteEmailPanel() {
  const { token, user, logout } = useAuth();
  const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8003').replace(/\/$/, '');
  const [items, setItems] = React.useState([]);
  const [loadingList, setLoadingList] = React.useState(false);
  const [error, setError] = React.useState('');
  const [actionPending, setActionPending] = React.useState(false);

  const [searchQuery, setSearchQuery] = React.useState('');
  const [filterStatus, setFilterStatus] = React.useState('');
  const [filterService, setFilterService] = React.useState('');
  const [dateFrom, setDateFrom] = React.useState('');
  const [dateTo, setDateTo] = React.useState('');
  const [page, setPage] = React.useState(1);

  const [expandedId, setExpandedId] = React.useState('');
  const [expandedDetail, setExpandedDetail] = React.useState(null);
  const [loadingExpanded, setLoadingExpanded] = React.useState(false);

  const [editOpen, setEditOpen] = React.useState(false);
  const [editInquiryId, setEditInquiryId] = React.useState('');
  const [editDetail, setEditDetail] = React.useState(null);
  const [editFinalPrice, setEditFinalPrice] = React.useState('');
  const [editLoadingNote, setEditLoadingNote] = React.useState('');
  const [editAdminNotes, setEditAdminNotes] = React.useState('');
  const [editCustomerNotes, setEditCustomerNotes] = React.useState('');

  const [selectedIds, setSelectedIds] = React.useState([]);
  const headerCheckboxRef = React.useRef(null);

  const [activeTab, setActiveTab] = React.useState('inquiries');
  const [emailTemplateKind, setEmailTemplateKind] = React.useState('final-offer');
  const [previewGuestName, setPreviewGuestName] = React.useState('Minta Ügyfél');
  const [previewPriceDisplay, setPreviewPriceDisplay] = React.useState('27940');
  const [previewLoadingDisplay, setPreviewLoadingDisplay] = React.useState(
    '4 000 Ft/fő/óra — a végösszeg az időtől függ; nem része az összesennek',
  );
  const [previewInsuranceDisplay, setPreviewInsuranceDisplay] = React.useState('0');
  const [previewNotes, setPreviewNotes] = React.useState('');
  const [previewOfficeEmail, setPreviewOfficeEmail] = React.useState('ugyfel@example.com');
  const [previewSubject, setPreviewSubject] = React.useState('');
  const [previewHtml, setPreviewHtml] = React.useState('');
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [previewError, setPreviewError] = React.useState('');
  const previewFormRef = React.useRef({});

  const [pricingDraft, setPricingDraft] = React.useState(() => normalizePricingSettings(DEFAULT_PRICING_SETTINGS));
  const [pricingLoading, setPricingLoading] = React.useState(false);
  const [pricingSaving, setPricingSaving] = React.useState(false);
  const [pricingError, setPricingError] = React.useState('');
  const [pricingSuccess, setPricingSuccess] = React.useState('');

  const authHeaders = React.useMemo(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    }),
    [token],
  );

  previewFormRef.current = {
    guestName: previewGuestName,
    priceDisplay: previewPriceDisplay,
    loadingDisplay: previewLoadingDisplay,
    insuranceDisplay: previewInsuranceDisplay,
    notes: previewNotes,
    officeEmail: previewOfficeEmail,
  };

  const isListTab = activeTab === 'inquiries' || activeTab === 'orders';
  const isOrdersTab = activeTab === 'orders';

  const orderedCount = React.useMemo(() => items.filter(isOrderedItem).length, [items]);
  const inquiryCount = Math.max(0, items.length - orderedCount);

  const loadEmailPreview = React.useCallback(async () => {
    if (!token || user?.role !== 'admin') return;
    const f = previewFormRef.current;
    setPreviewLoading(true);
    setPreviewError('');
    try {
      let path = '';
      const params = new URLSearchParams();
      if (emailTemplateKind === 'final-offer') {
        path = 'final-offer';
        params.set('customerName', f.guestName);
        params.set('priceDisplay', f.priceDisplay);
        params.set('loadingDisplay', f.loadingDisplay);
        params.set('insuranceDisplay', f.insuranceDisplay);
        params.set('notes', f.notes);
      } else if (emailTemplateKind === 'rejection') {
        path = 'rejection';
        params.set('customerName', f.guestName);
      } else if (emailTemplateKind === 'office') {
        path = 'office-notification';
        params.set('contactName', f.guestName);
        params.set('contactEmail', f.officeEmail);
      } else if (emailTemplateKind === 'customer') {
        path = 'customer-confirmation';
        params.set('customerName', f.guestName);
      }
      const res = await fetch(`${apiBase}/api/admin/email-preview/${path}?${params}`, {
        headers: authHeaders,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Előnézet betöltése sikertelen.');
      setPreviewSubject(data.subject || '');
      setPreviewHtml(data.html || '');
    } catch (e) {
      setPreviewError(e.message || 'Előnézet betöltése sikertelen.');
      setPreviewSubject('');
      setPreviewHtml('');
    } finally {
      setPreviewLoading(false);
    }
  }, [apiBase, authHeaders, emailTemplateKind, token, user]);

  React.useEffect(() => {
    if (activeTab !== 'email' || user?.role !== 'admin') return;
    void loadEmailPreview();
  }, [activeTab, emailTemplateKind, user, loadEmailPreview]);

  const loadPricingSettings = React.useCallback(async () => {
    if (!token || user?.role !== 'admin') return;
    setPricingLoading(true);
    setPricingError('');
    setPricingSuccess('');
    try {
      const res = await fetch(`${apiBase}/api/admin/pricing-settings`, { headers: authHeaders });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Árbeállítások betöltése sikertelen.');
      setPricingDraft(normalizePricingSettings(data?.settings));
    } catch (e) {
      setPricingError(e.message || 'Árbeállítások betöltése sikertelen.');
      setPricingDraft(normalizePricingSettings(DEFAULT_PRICING_SETTINGS));
    } finally {
      setPricingLoading(false);
    }
  }, [apiBase, authHeaders, token, user]);

  React.useEffect(() => {
    if (activeTab !== 'pricing' || user?.role !== 'admin') return;
    void loadPricingSettings();
  }, [activeTab, user, loadPricingSettings]);

  React.useEffect(() => {
    setPage(1);
    setSelectedIds([]);
    setExpandedId('');
    setExpandedDetail(null);
    setLoadingExpanded(false);
  }, [activeTab]);

  function updatePricing(path, value) {
    setPricingDraft((current) => {
      const next = JSON.parse(JSON.stringify(current));
      let node = next;
      for (let i = 0; i < path.length - 1; i += 1) node = node[path[i]];
      node[path[path.length - 1]] = value;
      return next;
    });
    setPricingSuccess('');
  }

  function pricingNumberValue(path) {
    let node = pricingDraft;
    for (const key of path) node = node?.[key];
    if (node == null) return '';
    return String(node);
  }

  function updatePricingNumber(path, raw) {
    const n = Number(String(raw).replace(',', '.'));
    updatePricing(path, raw === '' ? '' : Number.isFinite(n) && n >= 0 ? n : 0);
  }

  function validatePricingDraft() {
    const normalized = normalizePricingSettings(pricingDraft);
    const values = [
      normalized.shared.minNetHuf,
      normalized.shared.missingDistanceFallbackNetHuf,
      normalized.shared.volumeThresholdM3,
      normalized.shared.insuranceMinNetHuf,
      normalized.shared.insuranceRatePercent,
      ...normalized.shared.ratesUnder1M3,
      ...normalized.shared.ratesOver1M3,
      normalized.ikea.budaorsSurchargeHuf,
      normalized.ikea.customAboveKg,
      ...Object.values(normalized.ikea.zoneWeightPrices).flat(),
      ...normalized.ikea.valueSurcharges.map((x) => x.fee),
      ...normalized.ikea.collectionFees.map((x) => x.fee),
      normalized.quickCalculator.ratePerKmHuf,
      normalized.quickCalculator.minNetHuf,
    ];
    if (values.some((n) => !Number.isFinite(Number(n)) || Number(n) < 0)) {
      return 'Minden díj mezőnek nem negatív számnak kell lennie.';
    }
    return '';
  }

  async function savePricingSettings() {
    const validation = validatePricingDraft();
    if (validation) {
      setPricingError(validation);
      return;
    }
    setPricingSaving(true);
    setPricingError('');
    setPricingSuccess('');
    try {
      const settings = normalizePricingSettings(pricingDraft);
      const res = await fetch(`${apiBase}/api/admin/pricing-settings`, {
        method: 'PATCH',
        headers: authHeaders,
        body: JSON.stringify({ settings }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Árbeállítások mentése sikertelen.');
      setPricingDraft(normalizePricingSettings(data?.settings));
      setPricingSuccess('Árbeállítások elmentve. Az űrlapok az új díjakkal számolnak.');
    } catch (e) {
      setPricingError(e.message || 'Árbeállítások mentése sikertelen.');
    } finally {
      setPricingSaving(false);
    }
  }

  const loadList = React.useCallback(async () => {
    setLoadingList(true);
    setError('');
    try {
      const res = await fetch(`${apiBase}/api/admin/inquiries?limit=500`, {
        headers: authHeaders,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Bejövő ajánlatkérések betöltése sikertelen.');
      setItems(data?.items || []);
    } catch (e) {
      setError(e.message || 'Bejövő ajánlatkérések betöltése sikertelen.');
    } finally {
      setLoadingList(false);
    }
  }, [apiBase, authHeaders]);

  const fetchInquiry = React.useCallback(
    async (id) => {
      const res = await fetch(`${apiBase}/api/admin/inquiries/${id}`, { headers: authHeaders });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Részletek lekérése sikertelen.');
      return data?.item || null;
    },
    [apiBase, authHeaders],
  );

  React.useEffect(() => {
    if (!token || user?.role !== 'admin') return;
    loadList();
  }, [token, user, loadList]);

  React.useEffect(() => {
    if (!expandedId || !token || user?.role !== 'admin') {
      setExpandedDetail(null);
      setLoadingExpanded(false);
      return;
    }
    let cancelled = false;
    fetchInquiry(expandedId)
      .then((item) => {
        if (!cancelled) setExpandedDetail(item);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || 'Részletek betöltése sikertelen.');
      })
      .finally(() => {
        if (!cancelled) setLoadingExpanded(false);
      });
    return () => {
      cancelled = true;
    };
  }, [expandedId, token, user, fetchInquiry]);

  const filteredItems = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const visible = items.filter((item) => {
      if (isOrdersTab) {
        if (!isOrderedItem(item)) return false;
      } else if (isOrderedItem(item)) {
        return false;
      }
      if (filterStatus && item.status !== filterStatus) return false;
      if (filterService && item.inquiryType !== filterService) return false;
      const relevantDate = isOrdersTab ? item.orderSubmittedAt : item.createdAt;
      if (dateFrom) {
        const t = new Date(relevantDate).getTime();
        if (t < new Date(dateFrom).setHours(0, 0, 0, 0)) return false;
      }
      if (dateTo) {
        const t = new Date(relevantDate).getTime();
        if (t > new Date(dateTo).setHours(23, 59, 59, 999)) return false;
      }
      if (!q) return true;
      const blob = [
        item.referenceCode,
        item.customerName,
        item.customerEmail,
        item.pickupAddress,
        item.deliveryAddress,
        item.itemsSummary,
        item.serviceType,
        item.inquiryType,
      ]
        .join(' ')
        .toLowerCase();
      return blob.includes(q);
    });
    if (!isOrdersTab) return visible;
    return [...visible].sort((a, b) => new Date(b.orderSubmittedAt).getTime() - new Date(a.orderSubmittedAt).getTime());
  }, [items, searchQuery, filterStatus, filterService, dateFrom, dateTo, isOrdersTab]);

  const totalFiltered = filteredItems.length;
  const pageCount = Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const paginatedItems = React.useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filteredItems.slice(start, start + PAGE_SIZE);
  }, [filteredItems, safePage]);

  const selectedSet = React.useMemo(() => new Set(selectedIds.map(String)), [selectedIds]);

  React.useEffect(() => {
    const valid = new Set(items.map((i) => String(i._id)));
    setSelectedIds((prev) => prev.filter((id) => valid.has(String(id))));
  }, [items]);

  const pageIdStrs = React.useMemo(() => paginatedItems.map((i) => String(i._id)), [paginatedItems]);
  const allPageSelected = pageIdStrs.length > 0 && pageIdStrs.every((id) => selectedSet.has(id));
  const somePageSelected = pageIdStrs.some((id) => selectedSet.has(id));

  React.useEffect(() => {
    const el = headerCheckboxRef.current;
    if (el) el.indeterminate = somePageSelected && !allPageSelected;
  }, [somePageSelected, allPageSelected]);

  const toggleRowSelected = React.useCallback((id, checked) => {
    const s = String(id);
    setSelectedIds((prev) => {
      const next = new Set(prev.map(String));
      if (checked) next.add(s);
      else next.delete(s);
      return Array.from(next);
    });
  }, []);

  const toggleSelectAllOnPage = React.useCallback(() => {
    if (allPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIdStrs.includes(String(id))));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev.map(String), ...pageIdStrs])));
    }
  }, [allPageSelected, pageIdStrs]);

  React.useEffect(() => {
    setPage((p) => Math.min(p, pageCount));
  }, [pageCount]);

  const toggleExpand = (id) => {
    setExpandedId((prev) => {
      if (String(prev) === String(id)) {
        setLoadingExpanded(false);
        setExpandedDetail(null);
        return '';
      }
      setLoadingExpanded(true);
      setExpandedDetail(null);
      return id;
    });
  };

  const openEditModal = async (id) => {
    setEditInquiryId(id);
    setEditOpen(true);
    setEditDetail(null);
    try {
      const item = await fetchInquiry(id);
      setEditDetail(item);
      setEditFinalPrice(
        item?.finalOfferPrice != null ? String(item.finalOfferPrice) : String(item?.estimatedPrice ?? ''),
      );
      const note = (item?.finalOfferLoadingNote || '').trim();
      const legacyHuf = item?.finalOfferLoadingHuf != null && item.finalOfferLoadingHuf > 0 ? item.finalOfferLoadingHuf : null;
      setEditLoadingNote(
        note || (legacyHuf != null ? `${formatHufNetto(legacyHuf)} (korábbi rögzítés — nem része az összesennek)` : ''),
      );
      setEditAdminNotes(item?.adminNotes || '');
      setEditCustomerNotes(item?.customerFacingNotes || '');
    } catch (e) {
      setError(e.message || 'Adatok betöltése sikertelen.');
      setEditOpen(false);
    }
  };

  const closeEditModal = React.useCallback(() => {
    setEditOpen(false);
    setEditInquiryId('');
    setEditDetail(null);
  }, []);

  const inqEditBackdropRef = React.useRef(null);

  React.useEffect(() => {
    const el = inqEditBackdropRef.current;
    if (!el || !editOpen) return undefined;
    const onWheel = (e) => {
      if (e.target !== el) return;
      e.preventDefault();
      window.scrollBy({ top: e.deltaY, left: e.deltaX, behavior: 'auto' });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [editOpen]);

  React.useEffect(() => {
    if (!editOpen) return undefined;
    const onKey = (ev) => {
      if (ev.key === 'Escape') closeEditModal();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editOpen, closeEditModal]);

  const handleAccept = async (id) => {
    setActionPending(true);
    setError('');
    try {
      const res = await fetch(`${apiBase}/api/admin/inquiries/${id}/accept`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Elfogadás sikertelen.');
      await loadList();
      if (String(expandedId) === String(id)) {
        const fresh = await fetchInquiry(id);
        setExpandedDetail(fresh);
      }
    } catch (e) {
      setError(e.message || 'Elfogadás sikertelen.');
    } finally {
      setActionPending(false);
    }
  };

  const handleReject = async (id) => {
    if (
      !window.confirm(
        'Biztosan elutasítod ezt az ajánlatkérést? A rendszer elutasító e-mailt küld a megadott címre.',
      )
    ) {
      return;
    }
    setActionPending(true);
    setError('');
    try {
      const res = await fetch(`${apiBase}/api/admin/inquiries/${id}/reject`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Elutasítás sikertelen.');
      await loadList();
      if (String(expandedId) === String(id)) {
        const fresh = await fetchInquiry(id);
        setExpandedDetail(fresh);
      }
    } catch (e) {
      setError(e.message || 'Elutasítás sikertelen.');
    } finally {
      setActionPending(false);
    }
  };

  const handleMarkOrdered = async (id) => {
    if (!window.confirm('Biztosan átteszed ezt a tételt a Megrendelve fülre? E-mail nem megy ki.')) {
      return;
    }
    setActionPending(true);
    setError('');
    try {
      const res = await fetch(`${apiBase}/api/admin/inquiries/${id}/mark-ordered`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Megrendelésre állítás sikertelen.');
      setSelectedIds((prev) => prev.filter((selectedId) => String(selectedId) !== String(id)));
      if (String(expandedId) === String(id)) {
        setExpandedId('');
        setExpandedDetail(null);
      }
      await loadList();
    } catch (e) {
      setError(e.message || 'Megrendelésre állítás sikertelen.');
    } finally {
      setActionPending(false);
    }
  };

  const handleSetOrderCompleted = async (id, completed) => {
    setActionPending(true);
    setError('');
    try {
      const res = await fetch(`${apiBase}/api/admin/inquiries/${id}/order-completed`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ completed }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Teljesítési státusz mentése sikertelen.');
      await loadList();
      if (String(expandedId) === String(id)) {
        const fresh = await fetchInquiry(id);
        setExpandedDetail(fresh);
      }
    } catch (e) {
      setError(e.message || 'Teljesítési státusz mentése sikertelen.');
    } finally {
      setActionPending(false);
    }
  };

  const handleBatchDelete = React.useCallback(async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    if (
      !window.confirm(
        `Biztosan törlöd a kijelölt ${ids.length} ajánlatkérést? Ez végleges — nem vonható vissza, e-mail nem megy ki.`,
      )
    ) {
      return;
    }
    setActionPending(true);
    setError('');
    try {
      const res = await fetch(`${apiBase}/api/admin/inquiries/batch-delete`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ ids }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Törlés sikertelen.');
      setSelectedIds([]);
      const idSet = new Set(ids.map(String));
      if (idSet.has(String(expandedId))) {
        setExpandedId('');
        setExpandedDetail(null);
      }
      if (idSet.has(String(editInquiryId))) {
        closeEditModal();
      }
      await loadList();
    } catch (e) {
      setError(e.message || 'Törlés sikertelen.');
    } finally {
      setActionPending(false);
    }
  }, [selectedIds, apiBase, authHeaders, expandedId, editInquiryId, closeEditModal, loadList]);

  const handleSendEditedOffer = async () => {
    if (!editInquiryId) return;
    const n = parseHufInput(editFinalPrice);
    if (!Number.isFinite(n) || n <= 0) {
      setError('Adj meg érvényes, pozitív szállítási árat (HUF, egész szám).');
      return;
    }
    setActionPending(true);
    setError('');
    try {
      const res = await fetch(`${apiBase}/api/admin/inquiries/${editInquiryId}/send-offer`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          finalOfferPrice: n,
          finalOfferLoadingNote: editLoadingNote.trim(),
          adminNotes: editAdminNotes,
          customerFacingNotes: editCustomerNotes,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Ajánlat küldése sikertelen.');
      closeEditModal();
      await loadList();
      if (String(expandedId) === String(editInquiryId)) {
        const fresh = await fetchInquiry(editInquiryId);
        setExpandedDetail(fresh);
      }
    } catch (e) {
      setError(e.message || 'Ajánlat küldése sikertelen.');
    } finally {
      setActionPending(false);
    }
  };

  const isPending = (item) => item?.status === 'pending';
  const canAccept = (item) => isPending(item) && !actionPending;
  const canEditOffer = (item) =>
    (isPending(item) || isOfferSentStatus(item?.status)) && !isOrderedItem(item) && !actionPending;
  const canReject = (item) =>
    item?.status !== 'rejected' && (isPending(item) || isOfferSentStatus(item?.status) || isOrderedItem(item)) && !actionPending;
  const canMarkOrdered = (item) => isOfferSentStatus(item?.status) && !isOrderedItem(item) && !actionPending;
  const canUpdateOrderCompletion = (item) => isOrderedItem(item) && !actionPending;

  const rangeStart = totalFiltered === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(safePage * PAGE_SIZE, totalFiltered);

  const detail = expandedDetail;
  const showExpanded = expandedId && detail && String(detail._id) === String(expandedId);
  const sharedPricing = pricingDraft.shared;
  const ikeaPricing = pricingDraft.ikea;
  const quickPricing = pricingDraft.quickCalculator;
  const pricingGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 12,
  };
  const pricingCardStyle = {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 14,
    padding: 16,
    marginBottom: 16,
  };
  const pricingInputStyle = { width: '100%', height: 40, padding: '0 10px' };
  const renderPricingNumberInput = (label, path, suffix = 'Ft', stepValue = '1') => (
    <label className="inq-email-label">
      <span>{label}</span>
      <input
        type="number"
        min="0"
        step={stepValue}
        className="inq-search"
        style={pricingInputStyle}
        value={pricingNumberValue(path)}
        onChange={(e) => updatePricingNumber(path, e.target.value)}
      />
      {suffix ? <small className="inq-note">{suffix}</small> : null}
    </label>
  );

  return (
    <main className="admin-email-page inq-dash">
      <div className="inq-shell">
        <header className="inq-header">
          <div className="inq-header-text">
            <h1 className="inq-title">Admin</h1>
            <p className="inq-subtitle">Ajánlatkérések és kimenő e-mail sablonok előnézete.</p>
          </div>
          <div className="inq-header-user">
            <span className="inq-user-email">Bejelentkezve mint: {user?.email || '—'}</span>
            <button type="button" className="inq-btn inq-btn--ghost" onClick={logout}>
            Kijelentkezés
          </button>
        </div>
        </header>

        {user?.role === 'admin' && (
          <nav className="inq-tabs" aria-label="Admin szekciók">
            <button
              type="button"
              className={`inq-tab${activeTab === 'inquiries' ? ' inq-tab--active' : ''}`}
              onClick={() => setActiveTab('inquiries')}
            >
              Ajánlatkérések ({inquiryCount})
            </button>
            <button
              type="button"
              className={`inq-tab${activeTab === 'orders' ? ' inq-tab--active' : ''}`}
              onClick={() => setActiveTab('orders')}
            >
              Megrendelve ({orderedCount})
            </button>
            <button
              type="button"
              className={`inq-tab${activeTab === 'email' ? ' inq-tab--active' : ''}`}
              onClick={() => setActiveTab('email')}
            >
              E-mail sablonok
            </button>
            <button
              type="button"
              className={`inq-tab${activeTab === 'pricing' ? ' inq-tab--active' : ''}`}
              onClick={() => setActiveTab('pricing')}
            >
              Árbeállítások
            </button>
          </nav>
        )}

        {error && isListTab && <div className="admin-error inq-error">{error}</div>}

        {user?.role === 'admin' && isListTab && (
          <>
            <p className="inq-tab-desc">
              {isOrdersTab
                ? 'Megrendelt fuvarok: ügyfél által véglegesített vagy adminból megrendeltnek jelölt tételek.'
                : 'Beérkező megkeresések: iroda és ügyfél e-mail a beküldéskor, majd ajánlat / elutasítás innen.'}
            </p>
            <div className="inq-toolbar">
              <div className="inq-search-wrap">
                <span className="inq-search-icon" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="7" />
                    <path d="M21 21l-4.3-4.3" />
                  </svg>
                </span>
                <input
                  type="search"
                  className="inq-search"
                  placeholder="Keresés név, e-mail, útvonal…"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(1);
                  }}
                  aria-label="Keresés"
                />
              </div>
              <select
                className="inq-select"
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setPage(1);
                }}
                aria-label="Státusz"
              >
                <option value="">Státusz: mind</option>
                <option value="pending">Függőben</option>
                <option value="offer_sent">Ajánlat elküldve</option>
                <option value="edited_offer_sent">Módosított ajánlat</option>
                <option value="rejected">Elutasítva</option>
              </select>
              <select
                className="inq-select"
                value={filterService}
                onChange={(e) => {
                  setFilterService(e.target.value);
                  setPage(1);
                }}
                aria-label="Szolgáltatás"
              >
                <option value="">Szolgáltatás: mind</option>
                {Object.entries(SERVICE_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <div className="inq-date-group">
                <span className="inq-date-icon" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <path d="M16 2v4M8 2v4M3 10h18" />
                  </svg>
                </span>
                <input
                  type="date"
                  className="inq-date"
                  value={dateFrom}
                  onChange={(e) => {
                    setDateFrom(e.target.value);
                    setPage(1);
                  }}
                  aria-label="Dátumtól"
                />
                <span className="inq-date-sep">–</span>
                <input
                  type="date"
                  className="inq-date"
                  value={dateTo}
                  onChange={(e) => {
                    setDateTo(e.target.value);
                    setPage(1);
                  }}
                  aria-label="Dátumig"
                />
              </div>
              {selectedIds.length > 0 ? (
                <button
                  type="button"
                  className="inq-btn inq-btn--danger"
                  onClick={handleBatchDelete}
                  disabled={actionPending || loadingList}
                  title="Kijelöltek végleges törlése"
                >
                  Kijelöltek törlése ({selectedIds.length})
                </button>
              ) : null}
              <button
                type="button"
                className="inq-btn inq-btn--refresh"
                onClick={loadList}
                disabled={loadingList}
                title="Frissítés"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                  <path d="M3 21v-5h5" />
                  <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                  <path d="M21 3v5h-5" />
                </svg>
                <span>{loadingList ? '…' : 'Frissítés'}</span>
              </button>
                  </div>

            <div className="inq-table-card">
              <div className="inq-table-scroll">
                <table className="inq-table">
                  <thead>
                    <tr>
                      <th className="inq-th-check" scope="col">
                        <input
                          ref={headerCheckboxRef}
                          type="checkbox"
                          checked={allPageSelected}
                          onChange={toggleSelectAllOnPage}
                          aria-label="Összes megjelenő sor kijelölése ezen az oldalon"
                          disabled={paginatedItems.length === 0 || loadingList}
                        />
                      </th>
                      <th className="inq-th-lead" aria-hidden="true" />
                      <th>Azonosító</th>
                      <th>Név</th>
                      <th>E-mail</th>
                      <th>Útvonal</th>
                      <th>Ajánlat, nettó (+ÁFA)</th>
                      <th>Státusz</th>
                      <th>{isOrdersTab ? 'Megrendelve' : 'Dátum'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedItems.length === 0 && (
                      <tr>
                        <td colSpan={9} className="inq-empty">
                          {loadingList ? 'Betöltés…' : isOrdersTab ? 'Nincs megrendelt tétel.' : 'Nincs találat.'}
                        </td>
                      </tr>
                    )}
                    {paginatedItems.map((item) => {
                      const expanded = String(expandedId) === String(item._id);
                      return (
                        <React.Fragment key={item._id}>
                          <tr
                            role="button"
                            tabIndex={0}
                            aria-expanded={expanded}
                            aria-label={`${valueOrDash(item.customerName)} — részletek ${expanded ? 'bezárása' : 'megnyitása'}`}
                            className={`inq-tr inq-tr--clickable${expanded ? ' inq-tr--expanded' : ''}${isPending(item) ? ' inq-tr--pending' : ''}`}
                            onClick={() => toggleExpand(item._id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggleExpand(item._id);
                              }
                            }}
                          >
                            <td
                              className="inq-td-check"
                              onClick={(e) => e.stopPropagation()}
                              onMouseDown={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={selectedSet.has(String(item._id))}
                                onChange={(e) => toggleRowSelected(item._id, e.target.checked)}
                                aria-label={`Kijelölés: ${valueOrDash(item.referenceCode)}`}
                                disabled={loadingList}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </td>
                            <td className="inq-td-lead" aria-hidden="true">
                              <div className="inq-td-lead-inner">
                                <span className={`inq-chevron${expanded ? ' inq-chevron--expanded' : ''}`}>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                </span>
                                {isPending(item) ? <span className="inq-accent-bar" /> : null}
                    </div>
                            </td>
                            <td className="inq-td-ref">{valueOrDash(item.referenceCode)}</td>
                            <td className="inq-td-strong">{valueOrDash(item.customerName)}</td>
                            <td>{valueOrDash(item.customerEmail)}</td>
                            <td className="inq-td-route">{routeLine(item)}</td>
                            <td>{adminTablePrice(item)}</td>
                            <td className="inq-td-status">
                              {isOrdersTab ? (
                                <span className={orderCompletionBadgeClass(item)}>{orderCompletionLabel(item)}</span>
                              ) : (
                                <>
                                  <span className={statusBadgeClass(item.status)}>{statusLabel(item.status)}</span>
                                  {isOrderedItem(item) ? <span className="inq-badge inq-badge--ordered">Megrendelve</span> : null}
                                </>
                              )}
                            </td>
                            <td className="inq-td-date">{formatYmd(isOrdersTab ? item.orderSubmittedAt : item.createdAt)}</td>
                          </tr>
                          {expanded && (
                            <tr className="inq-tr-expand">
                              <td colSpan={9}>
                                <div className="inq-expand">
                                  {loadingExpanded && <p className="inq-note">Részletek betöltése…</p>}
                                  {!loadingExpanded && expandedId && !expandedDetail && (
                                    <p className="inq-note">Nem sikerült betölteni a részleteket.</p>
                                  )}
                                  {!loadingExpanded && showExpanded && detail && (
                                    <>
                                      <div className="inq-expand-grid">
                                        <section className="inq-panel">
                                          <h3 className="inq-panel-title">Ügyfél adatok</h3>
                                          <dl className="inq-dl">
                                            <dt>Azonosító</dt>
                                            <dd>{valueOrDash(detail.referenceCode)}</dd>
                                            <dt>Név</dt>
                                            <dd>{valueOrDash(detail.customerName)}</dd>
                                            <dt>E-mail</dt>
                                            <dd>{valueOrDash(detail.customerEmail)}</dd>
                                            <dt>Telefon</dt>
                                            <dd>{valueOrDash(detail.customerPhone)}</dd>
                                          </dl>
                                        </section>
                                        <section className="inq-panel">
                                          <h3 className="inq-panel-title">Szállítás részletei</h3>
                                          <dl className="inq-dl">
                                            <dt>Szolgáltatás / típus</dt>
                                            <dd>{serviceTypeCell(detail)}</dd>
                                            <dt>Felvétel (honnan)</dt>
                                            <dd>{valueOrDash(detail.pickupAddress || detail.formPayload?.route?.fromAddress)}</dd>
                                            <dt>Címzett (hová)</dt>
                                            <dd>{valueOrDash(detail.deliveryAddress || detail.formPayload?.route?.toAddress)}</dd>
                                            <dt>Szállítási méret</dt>
                                            <dd style={{ whiteSpace: 'pre-line' }}>{shipmentSizeLine(detail)}</dd>
                                            {detail.distanceKm != null && (
                                              <>
                                                <dt>Távolság</dt>
                                                <dd>{detail.distanceKm} km</dd>
                                              </>
                                            )}
                                            {(() => {
                                              const att = attachmentNamesFromFormPayload(detail.formPayload);
                                              return (
                                                <>
                                                  <dt>Csatolmányok</dt>
                                                  <dd>
                                                    {att.length ? (
                                                      <ul className="inq-attachments-list">
                                                        {att.map((name, i) => (
                                                          <li key={`${name}-${i}`}>{name}</li>
                                                        ))}
                                                      </ul>
                                                    ) : (
                                                      'nincs'
                                                    )}
                                                    {att.length ? (
                                                      <p className="inq-attachments-hint">
                                                        A fájlok a beküldéskor kiküldött iroda e-mailben érkeztek meg csatolmányként.
                                                      </p>
                                                    ) : null}
                                                  </dd>
                                                </>
                                              );
                                            })()}
                                          </dl>
                                        </section>
                                        <section className="inq-panel">
                                          <h3 className="inq-panel-title">Árazás</h3>
                                          <dl className="inq-dl">
                                            <dt>Becsült nettó</dt>
                                            <dd>{formatHufNetto(detail.estimatedPrice)}</dd>
                                            {(() => {
                                              const ex = ugyfelExtraFlags(detail.formPayload);
                                              return (
                                                <>
                                                  {ex.wantsRakodas ? (
                                                    <>
                                                      <dt style={{ color: '#9a3412', fontWeight: 700 }}>Ügyfél: rakodás</dt>
                                                      <dd style={{ color: '#7c2d12' }}>
                                                        igen — a tájékoztató szöveget add meg (nem része az összesennek)
                                                      </dd>
                                                    </>
                                                  ) : null}
                                                  {ex.wantsInsurance ? (
                                                    <>
                                                      <dt style={{ color: '#9a3412', fontWeight: 700 }}>Ügyfél: biztosítás</dt>
                                                      <dd style={{ color: '#7c2d12' }}>
                                                        igen — tedd be a biztosítás díját az ajánlatban
                                                      </dd>
                                                    </>
                                                  ) : null}
                                                </>
                                              );
                                            })()}
                                            <dt>Szállítás (végső, nettó)</dt>
                                            <dd>{detail.finalOfferPrice != null ? formatHufNetto(detail.finalOfferPrice) : '—'}</dd>
                                            <dt>Rakodás (tájékoztató)</dt>
                                            <dd>
                                              {(detail.finalOfferLoadingNote || '').trim() ||
                                                (detail.finalOfferLoadingHuf != null && detail.finalOfferLoadingHuf > 0
                                                  ? `${formatHufNetto(detail.finalOfferLoadingHuf)} (korábbi rögzítés)`
                                                  : '—')}
                                            </dd>
                                            <dt>Biztosítás (végső, nettó)</dt>
                                            <dd>
                                              {insuranceHufForItem(detail) > 0
                                                ? formatHufNetto(insuranceHufForItem(detail))
                                                : '—'}
                                            </dd>
                                            <dt>Összesen (nettó)</dt>
                                            <dd>
                                              {detail.finalOfferPrice != null
                                                ? formatHufNetto(
                                                    Number(detail.finalOfferPrice) +
                                                      insuranceHufForItem(detail),
                                                  )
                                                : '—'}
                                            </dd>
                                            <dt>Megjegyzés</dt>
                                            <dd>{valueOrDash(detail.extraNotes)}</dd>
                                          </dl>
                                        </section>
                                        <section className="inq-panel inq-panel--email">
                                          <div className="inq-panel-email-head">
                                            <h3 className="inq-panel-title">E-mail előnézet / státusz</h3>
                                            {isOrdersTab ? (
                                              <span className={orderCompletionBadgeClass(detail)}>{orderCompletionLabel(detail)}</span>
                                            ) : (
                                              <span className={statusBadgeClass(detail.status)}>{statusLabel(detail.status)}</span>
                                            )}
                    </div>
                                          <ul className="inq-timeline">
                                            <li className="inq-timeline-item">
                                              <span className="inq-timeline-dot" style={{ background: INQ_ORANGE }} />
                    <div>
                                                <div className="inq-timeline-title">Iroda értesítése</div>
                                                <div className="inq-timeline-meta">
                                                  <strong
                                                    className={
                                                      detail.submissionNotifyOfficeError ? 'admin-text-error' : ''
                                                    }
                                                  >
                                                    {emailSentLabel(
                                                      detail.submissionNotifyOfficeAt,
                                                      detail.submissionNotifyOfficeError,
                                                    )}
                      </strong>
                                                  {detail.submissionNotifyOfficeAt && (
                                                    <span>{formatYmdHm(detail.submissionNotifyOfficeAt)}</span>
                                                  )}
                    </div>
                                                {detail.submissionNotifyOfficeError && (
                                                  <p className="inq-timeline-err">{detail.submissionNotifyOfficeError}</p>
                                                )}
                    </div>
                                            </li>
                                            <li className="inq-timeline-item">
                                              <span className="inq-timeline-dot" style={{ background: INQ_ORANGE }} />
                    <div>
                                                <div className="inq-timeline-title">Ügyfél visszaigazolás</div>
                                                <div className="inq-timeline-meta">
                                                  <strong className={detail.submissionReceiptError ? 'admin-text-error' : ''}>
                                                    {emailSentLabel(
                                                      detail.submissionReceiptAt,
                                                      detail.submissionReceiptError,
                                                    )}
                      </strong>
                                                  {detail.submissionReceiptAt && (
                                                    <span>{formatYmdHm(detail.submissionReceiptAt)}</span>
                                                  )}
                    </div>
                                                {detail.submissionReceiptError && (
                                                  <p className="inq-timeline-err">{detail.submissionReceiptError}</p>
                                                )}
                    </div>
                                            </li>
                                            {detail.orderSubmittedAt && (
                                              <li className="inq-timeline-item">
                                                <span className="inq-timeline-dot" style={{ background: '#059669' }} />
                                                <div>
                                                  <div className="inq-timeline-title">Megrendelés</div>
                                                  <div className="inq-timeline-meta">
                                                    <strong>Megrendelve</strong>
                                                    <span>{formatYmdHm(detail.orderSubmittedAt)}</span>
                                                  </div>
                                                </div>
                                              </li>
                                            )}
                                            {detail.orderCompletedAt && (
                                              <li className="inq-timeline-item">
                                                <span className="inq-timeline-dot" style={{ background: '#2563eb' }} />
                                                <div>
                                                  <div className="inq-timeline-title">Teljesítés</div>
                                                  <div className="inq-timeline-meta">
                                                    <strong>Teljesítve</strong>
                                                    <span>{formatYmdHm(detail.orderCompletedAt)}</span>
                                                  </div>
                                                </div>
                                              </li>
                                            )}
                                          </ul>
                                          <p className="inq-type-foot">Típus: {valueOrDash(detail.inquiryType)}</p>
                                        </section>
                    </div>
                                      <div className="inq-expand-actions">
                                        <button
                                          type="button"
                                          className="inq-btn inq-btn--primary"
                                          disabled={!canAccept(detail)}
                                          onClick={() => handleAccept(detail._id)}
                                        >
                                          Elfogadás
                                        </button>
                                        <button
                                          type="button"
                                          className="inq-btn inq-btn--outline"
                                          disabled={!canEditOffer(detail)}
                                          onClick={() => openEditModal(detail._id)}
                                        >
                                          {isOfferSentStatus(detail.status) ? 'Ár módosítása / újraküldés' : 'Ár szerkesztése'}
                                        </button>
                                        <button
                                          type="button"
                                          className="inq-btn inq-btn--success"
                                          disabled={!canMarkOrdered(detail)}
                                          onClick={() => handleMarkOrdered(detail._id)}
                                        >
                                          Megrendelve
                                        </button>
                                        {isOrderedItem(detail) ? (
                                          <button
                                            type="button"
                                            className={`inq-btn${isOrderCompleted(detail) ? ' inq-btn--outline' : ' inq-btn--success'}`}
                                            disabled={!canUpdateOrderCompletion(detail)}
                                            onClick={() => handleSetOrderCompleted(detail._id, !isOrderCompleted(detail))}
                                          >
                                            {isOrderCompleted(detail) ? 'Nem teljesítve' : 'Teljesítve'}
                                          </button>
                                        ) : null}
                                        <button
                                          type="button"
                                          className="inq-btn inq-btn--reject"
                                          disabled={!canReject(detail)}
                                          onClick={() => handleReject(detail._id)}
                                        >
                                          Elutasítás
                                        </button>
                    </div>
                                    </>
                                  )}
                    </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
                    </div>
                  </div>

            <footer className="inq-footer">
              <span className="inq-footer-count">
                {totalFiltered === 0 ? '0 / 0 találat' : `${rangeStart}–${rangeEnd} / ${totalFiltered} találat`}
              </span>
              <div className="inq-pagination">
                    <button
                      type="button"
                  className="inq-page-btn"
                  disabled={safePage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  aria-label="Előző oldal"
                >
                  ‹
                    </button>
                <span className="inq-page-current">{safePage}</span>
                    <button
                      type="button"
                  className="inq-page-btn"
                  disabled={safePage >= pageCount}
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                  aria-label="Következő oldal"
                >
                  ›
                    </button>
                  </div>
            </footer>
          </>
        )}

        {user?.role === 'admin' && activeTab === 'pricing' && (
          <div className="inq-email-tab">
            <p className="inq-tab-desc">
              Ezek a díjak hajtják meg az összes árajánlatkérő űrlapot és a főoldali gyors kalkulátort. Mentés után az új
              kalkulációk már az itt rögzített nettó árakkal számolnak.
            </p>
            {pricingError && <div className="admin-error inq-error">{pricingError}</div>}
            {pricingSuccess && <div className="qf-banner qf-banner--success">{pricingSuccess}</div>}
            {pricingLoading ? (
              <p className="inq-note">Árbeállítások betöltése…</p>
            ) : (
              <>
                <section style={pricingCardStyle}>
                  <h2 className="inq-section-title">Shared szállítás</h2>
                  <div style={pricingGridStyle}>
                    {renderPricingNumberInput('Minimum nettó díj', ['shared', 'minNetHuf'])}
                    {renderPricingNumberInput('Távolság nélküli fallback', ['shared', 'missingDistanceFallbackNetHuf'])}
                    {renderPricingNumberInput('Térfogat küszöb', ['shared', 'volumeThresholdM3'], 'm³')}
                    {renderPricingNumberInput('Biztosítás minimum nettó díj', ['shared', 'insuranceMinNetHuf'])}
                    {renderPricingNumberInput('Biztosítás díjszázalék', ['shared', 'insuranceRatePercent'], '%', '0.1')}
                  </div>
                  <div style={{ overflowX: 'auto', marginTop: 16 }}>
                    <table className="inq-table">
                      <thead>
                        <tr>
                          <th>Súlysáv</th>
                          <th>1 m³ alatt (Ft/km)</th>
                          <th>1 m³ felett (Ft/km)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sharedPricing.weightBands.map((band, idx) => (
                          <tr key={band.label}>
                            <td>{band.label}</td>
                            <td>
                              <input
                                type="number"
                                min="0"
                                className="inq-search"
                                style={pricingInputStyle}
                                value={pricingNumberValue(['shared', 'ratesUnder1M3', idx])}
                                onChange={(e) => updatePricingNumber(['shared', 'ratesUnder1M3', idx], e.target.value)}
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                min="0"
                                className="inq-search"
                                style={pricingInputStyle}
                                value={pricingNumberValue(['shared', 'ratesOver1M3', idx])}
                                onChange={(e) => updatePricingNumber(['shared', 'ratesOver1M3', idx], e.target.value)}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                <section style={pricingCardStyle}>
                  <h2 className="inq-section-title">IKEA</h2>
                  <div style={pricingGridStyle}>
                    {renderPricingNumberInput('Budaörs felár', ['ikea', 'budaorsSurchargeHuf'])}
                    {renderPricingNumberInput('Egyedi ajánlat súly felett', ['ikea', 'customAboveKg'], 'kg')}
                  </div>
                  <div style={{ overflowX: 'auto', marginTop: 16 }}>
                    <table className="inq-table">
                      <thead>
                        <tr>
                          <th>Zóna</th>
                          {ikeaPricing.weightBands.map((band) => (
                            <th key={band.label}>{band.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {ikeaPricing.zones
                          .filter((zone) => Array.isArray(ikeaPricing.zoneWeightPrices[zone.value]))
                          .map((zone) => (
                            <tr key={zone.value}>
                              <td>{zone.label}</td>
                              {ikeaPricing.weightBands.map((band, idx) => (
                                <td key={`${zone.value}-${band.label}`}>
                                  <input
                                    type="number"
                                    min="0"
                                    className="inq-search"
                                    style={pricingInputStyle}
                                    value={pricingNumberValue(['ikea', 'zoneWeightPrices', zone.value, idx])}
                                    onChange={(e) =>
                                      updatePricingNumber(['ikea', 'zoneWeightPrices', zone.value, idx], e.target.value)
                                    }
                                  />
                                </td>
                              ))}
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ ...pricingGridStyle, marginTop: 16 }}>
                    {ikeaPricing.valueSurcharges.map((row, idx) => (
                      <React.Fragment key={row.value}>
                        {renderPricingNumberInput(row.label, ['ikea', 'valueSurcharges', idx, 'fee'])}
                      </React.Fragment>
                    ))}
                    {ikeaPricing.collectionFees.map((row, idx) => (
                      <React.Fragment key={row.value}>
                        {renderPricingNumberInput(row.label, ['ikea', 'collectionFees', idx, 'fee'])}
                      </React.Fragment>
                    ))}
                  </div>
                </section>

                <section style={pricingCardStyle}>
                  <h2 className="inq-section-title">Főoldali gyors kalkulátor</h2>
                  <div style={pricingGridStyle}>
                    {renderPricingNumberInput('Ft / km', ['quickCalculator', 'ratePerKmHuf'])}
                    {renderPricingNumberInput('Minimum nettó díj', ['quickCalculator', 'minNetHuf'])}
                  </div>
                  <p className="inq-note">
                    Jelenlegi gyors kalkulátor: {formatHuf(quickPricing.ratePerKmHuf)} / km, minimum{' '}
                    {formatHuf(quickPricing.minNetHuf)} nettó.
                  </p>
                </section>

                <div className="inq-email-toolbar">
                  <button
                    type="button"
                    className="inq-btn inq-btn--primary"
                    onClick={() => void savePricingSettings()}
                    disabled={pricingSaving}
                  >
                    {pricingSaving ? 'Mentés…' : 'Árbeállítások mentése'}
                  </button>
                  <button
                    type="button"
                    className="inq-btn inq-btn--ghost"
                    onClick={() => void loadPricingSettings()}
                    disabled={pricingSaving || pricingLoading}
                  >
                    Újratöltés
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {user?.role === 'admin' && activeTab === 'email' && (
          <div className="inq-email-tab">
            <p className="inq-tab-desc">
              A HTML a backend ugyanazzal a sablonnal készül, mint az éles küldés. Szöveget a{' '}
              <code className="inq-code">backend/emailTemplates/inquiryOfferTemplates.js</code> és a{' '}
              <code className="inq-code">backend/utils/email.js</code> fájlokban szerkeszd.
            </p>
            {previewError && <div className="admin-error inq-error">{previewError}</div>}
            <div className="inq-email-toolbar">
              <label className="inq-email-label">
                <span>Sablon</span>
                <select
                  className="inq-select"
                  value={emailTemplateKind}
                  onChange={(e) => setEmailTemplateKind(e.target.value)}
                >
                  <option value="final-offer">Végső ajánlat (ügyfél)</option>
                  <option value="rejection">Elutasítás (ügyfél)</option>
                  <option value="office">Új beérkezés (iroda)</option>
                  <option value="customer">Visszaigazolás (ügyfél, beküldés után)</option>
                </select>
              </label>
              <label className="inq-email-label">
                <span>Ügyfél neve</span>
                <input
                  type="text"
                  className="inq-search"
                  style={{ height: 40, paddingLeft: 12 }}
                  value={previewGuestName}
                  onChange={(e) => setPreviewGuestName(e.target.value)}
                />
              </label>
              {emailTemplateKind === 'final-offer' && (
                <>
                  <label className="inq-email-label">
                    <span>Szállítás (HUF megjelenítés)</span>
                    <input
                      type="text"
                      className="inq-search"
                      style={{ height: 40, paddingLeft: 12 }}
                      inputMode="numeric"
                      value={previewPriceDisplay}
                      onChange={(e) => setPreviewPriceDisplay(filterHufDigitsInput(e.target.value))}
                      placeholder="27940"
                    />
                  </label>
                  <label className="inq-email-label">
                    <span>Rakodás (tájékoztató, szabad szöveg)</span>
                    <input
                      type="text"
                      className="inq-search"
                      style={{ height: 40, paddingLeft: 12 }}
                      value={previewLoadingDisplay}
                      onChange={(e) => setPreviewLoadingDisplay(e.target.value)}
                      placeholder="0"
                    />
                  </label>
                  <label className="inq-email-label">
                    <span>Biztosítás (HUF)</span>
                    <input
                      type="text"
                      className="inq-search"
                      style={{ height: 40, paddingLeft: 12 }}
                      inputMode="numeric"
                      value={previewInsuranceDisplay}
                      onChange={(e) => setPreviewInsuranceDisplay(filterHufDigitsInput(e.target.value))}
                      placeholder="0"
                    />
                  </label>
                  <label className="inq-email-label inq-email-label--wide">
                    <span>Megjegyzés az ügyfélnek (opcionális)</span>
                    <textarea
                      className="inq-email-textarea"
                      rows={2}
                      value={previewNotes}
                      onChange={(e) => setPreviewNotes(e.target.value)}
                    />
                  </label>
                </>
              )}
              {emailTemplateKind === 'office' && (
                <label className="inq-email-label">
                  <span>Ügyfél e-mail (irodai levélben)</span>
                  <input
                    type="email"
                    className="inq-search"
                    style={{ height: 40, paddingLeft: 12 }}
                    value={previewOfficeEmail}
                    onChange={(e) => setPreviewOfficeEmail(e.target.value)}
                  />
                </label>
              )}
              <button
                type="button"
                className="inq-btn inq-btn--primary inq-email-refresh"
                onClick={() => void loadEmailPreview()}
                disabled={previewLoading}
              >
                {previewLoading ? 'Frissítés…' : 'Előnézet frissítése'}
              </button>
            </div>
            <div className="inq-email-preview-card">
              <div className="inq-email-subject-row">
                <span className="inq-email-subject-label">Tárgy</span>
                <strong className="inq-email-subject-value">{previewSubject || '—'}</strong>
                    </div>
              {previewLoading && !previewHtml && <p className="inq-note">Betöltés…</p>}
              {previewHtml ? (
                <iframe title="E-mail HTML előnézet" className="inq-email-iframe" srcDoc={previewHtml} />
              ) : (
                !previewLoading && <p className="inq-note">Nincs előnézet. Válassz sablont, majd frissíts.</p>
              )}
                    </div>
                  </div>
        )}

        {editOpen && (
          <div
            ref={inqEditBackdropRef}
            className="admin-modal-backdrop inq-modal-backdrop"
            role="presentation"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) closeEditModal();
            }}
          >
            <div
              className="admin-modal inq-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="admin-edit-title"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="inq-modal__header">
                <h2 id="admin-edit-title">Ár szerkesztése — végső ajánlat</h2>
                <button type="button" className="inq-modal__close" aria-label="Bezárás" onClick={closeEditModal}>
                  <span aria-hidden="true">×</span>
                </button>
              </div>
              {!editDetail ? (
                <p className="inq-note">Adatok betöltése…</p>
              ) : (
                (() => {
                  const ex = ugyfelExtraFlags(editDetail.formPayload);
                  const insuranceHuf = insuranceHufForItem(editDetail);
                  const insuranceValue = String(editDetail.formPayload?.insurance?.shipmentValue || '').trim();
                  return (
                    <>
                      <p className="admin-modal-sub">
                        Becsült nettó összeg (űrlap): <strong>{formatHufNetto(editDetail.estimatedPrice)}</strong>. A
                        szállítási díj kötelező (minden összeg <strong>nettó</strong>, <strong>+ ÁFA</strong>). A{' '}
                        <strong>rakodás</strong> csak tájékoztató szöveg az e-mailben (pl. óradíj);{' '}
                        <strong>nem adódik hozzá</strong> az összesenhez. A <strong>biztosítás</strong> automatikusan
                        számolódik a küldemény értékéből, és hozzáadódik az összesenhez.
                      </p>
                      <p className="inq-note" style={{ marginTop: 0, marginBottom: 12 }}>
                        <strong>Iroda:</strong> a végső ajánlat e-mail másolatban a beküldési értesítő címre is kimegy
                        (BCC), így látjátok, mit kapott az ügyfél.
                      </p>
                      {(() => {
                        const att = attachmentNamesFromFormPayload(editDetail.formPayload);
                        return (
                          <div
                            className="inq-modal-attachments"
                            style={{
                              marginBottom: 14,
                              padding: '10px 12px',
                              borderRadius: 8,
                              border: '1px solid #e5e7eb',
                              background: '#f9fafb',
                              fontSize: 14,
                              lineHeight: 1.45,
                            }}
                          >
                            <strong style={{ display: 'block', marginBottom: 6 }}>Csatolmányok</strong>
                            {att.length ? (
                              <>
                                <ul className="inq-attachments-list" style={{ marginTop: 0 }}>
                                  {att.map((name, i) => (
                                    <li key={`${name}-${i}`}>{name}</li>
                                  ))}
                                </ul>
                                <p className="inq-note" style={{ margin: '8px 0 0', fontSize: 12 }}>
                                  A fájlok a beküldéskor kiküldött iroda e-mailben vannak meg csatolmányként.
                                </p>
                              </>
                            ) : (
                              <span className="inq-note" style={{ margin: 0 }}>
                                nincs
                              </span>
                            )}
                          </div>
                        );
                      })()}
                      {ex.wantsRakodas || ex.wantsInsurance ? (
                        <div
                          className="inq-extras-alert"
                          style={{
                            marginBottom: 14,
                            padding: '12px 14px',
                            borderRadius: 10,
                            border: '1px solid #fed7aa',
                            background: '#fff7ed',
                            color: '#9a3412',
                            fontSize: 14,
                            lineHeight: 1.5,
                          }}
                        >
                          <strong>Figyelem — az ügyfél az űrlapon ezt jelölte</strong>
                          <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                            {ex.wantsRakodas ? (
                              <li>
                                <strong>Rakodás / házon belüli segítség: igen</strong> — írd le tájékoztatóként (pl. óradíj);
                                nem része a nettó összesennek.
                              </li>
                            ) : null}
                            {ex.wantsInsurance ? (
                              <li>
                                <strong>Biztosítás: igen</strong> — automatikus díj:{' '}
                                {insuranceHuf > 0 ? formatHufNetto(insuranceHuf) : '—'} (érték:{' '}
                                {insuranceValue ? `${insuranceValue} Ft` : 'nincs megadva'}).
                              </li>
                            ) : null}
                          </ul>
                        </div>
                      ) : null}
                      <label className="admin-modal-field">
                        <span>Szállítási árajánlat (HUF, nettó, kötelező)</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={editFinalPrice}
                          onChange={(e) => setEditFinalPrice(filterHufDigitsInput(e.target.value))}
                          autoComplete="off"
                        />
                      </label>
                      <label className="admin-modal-field">
                        <span
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: 8,
                            marginBottom: 2,
                          }}
                        >
                          <span>Rakodás — tájékoztató (nem része az összesennek)</span>
                          <span style={ugyfelUrlapPillStyle(ex.wantsRakodas)}>
                            Ügyfél az űrlapon: {ex.wantsRakodas ? 'igen, kérte' : 'nem kérte'}
                          </span>
                        </span>
                        <textarea
                          rows={2}
                          value={editLoadingNote}
                          onChange={(e) => setEditLoadingNote(e.target.value)}
                          placeholder="pl. 4 000 Ft/fő/óra — a végösszeg az eltelt idő alapján"
                          autoComplete="off"
                        />
                      </label>
                      <div className="admin-modal-field">
                        <span
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: 8,
                            marginBottom: 2,
                          }}
                        >
                          <span>Biztosítás díja (automatikus, nettó)</span>
                          <span style={ugyfelUrlapPillStyle(ex.wantsInsurance)}>
                            Ügyfél az űrlapon: {ex.wantsInsurance ? 'igen, kérte' : 'nem kérte'}
                          </span>
                        </span>
                        <strong>{insuranceHuf > 0 ? formatHufNetto(insuranceHuf) : '—'}</strong>
                        {ex.wantsInsurance ? (
                          <p className="inq-note" style={{ margin: '6px 0 0', fontSize: 12 }}>
                            Minimum díj vagy a küldemény értékének beállított százaléka közül a nagyobb. A szabály az
                            Árbeállítások fülön módosítható.
                          </p>
                        ) : null}
                      </div>
                      <p className="admin-modal-sub" style={{ marginTop: 0 }}>
                        <strong>Összesen (nettó):</strong>{' '}
                        {(() => {
                          const shipRaw = String(editFinalPrice ?? '').trim();
                          if (shipRaw === '') return '—';
                          const a = parseHufInput(shipRaw);
                          if (!Number.isFinite(a)) return '—';
                          return formatHufNetto(a + insuranceHuf);
                        })()}
                      </p>
                      <p className="admin-modal-sub" style={{ marginTop: 6, fontSize: '0.88rem' }}>
                        A rakodás mező csak szöveg az ügyfélnek; az összesen (nettó) = szállítás + automatikus
                        biztosítás; az ÁFA a felirat szerint értendő.
                      </p>
                      <label className="admin-modal-field">
                        <span>Belső jegyzet (nem megy ki e-mailben)</span>
                        <textarea rows={3} value={editAdminNotes} onChange={(e) => setEditAdminNotes(e.target.value)} />
                      </label>
                      <label className="admin-modal-field">
                        <span>Megjegyzés az ügyfélnek (az ajánlat e-mailben megjelenik)</span>
                        <textarea
                          rows={3}
                          value={editCustomerNotes}
                          onChange={(e) => setEditCustomerNotes(e.target.value)}
                        />
                      </label>
                      <div className="admin-modal-actions">
                        <button type="button" className="secondary-btn" onClick={closeEditModal} disabled={actionPending}>
                          Mégse
                        </button>
                        <button
                          type="button"
                          className="primary-btn"
                          onClick={handleSendEditedOffer}
                          disabled={actionPending}
                        >
                          {actionPending ? 'Küldés…' : 'Ajánlat küldése'}
                        </button>
                      </div>
                    </>
                  );
                })()
              )}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
